import { QueryClient } from '@tanstack/react-query';
import type { CubeQuery, CubeQueryRecord } from '@/dev/types/cubeQueryTypes';
import { CacheSubscriber } from '@/dev/services/CacheSubscriber';
import { clearRequestTimings, removeAxiosInterceptor, setupAxiosInterceptor } from '@/dev/services/queryInterceptor';
import type { CubejsApi } from '@/dev/services/RecordingState';
import { QueryStore } from '@/dev/services/QueryStore';
import { TabStateManager } from '@/dev/services/TabStateManager';

const NETWORK_CACHE_DUPLICATE_WINDOW_MS = 1_000;

export class QueryRecorder {
  private cubejsApi: CubejsApi | null = null;

  private queryClient: QueryClient | null = null;

  private cacheSubscriber: CacheSubscriber;

  private recentNetworkQueryHashes = new Map<string, number[]>();

  constructor(
    private queryStore: QueryStore,
    private tabStateManager: TabStateManager,
  ) {
    this.cacheSubscriber = new CacheSubscriber(
      queryStore,
      tabStateManager,
      () => this.cubejsApi?.apiUrl || '',
      queryHash => this.consumeRecentNetworkQuery(queryHash),
      () => this.generateRequestId(),
    );
  }

  setCubejsApi(api: CubejsApi): void {
    this.cubejsApi = api;
  }

  setQueryClient(client: QueryClient): void {
    this.queryClient = client;
    this.cacheSubscriber.setQueryClient(client);
  }

  extractQueryKeyFromReactQuery(cubeQuery: CubeQuery): string[] | undefined {
    if (!this.queryClient) return undefined;

    try {
      const cache = this.queryClient.getQueryCache();
      const allQueries = cache.getAll();
      const queryStr = JSON.stringify(cubeQuery);

      const match = allQueries.find(q =>
        q.queryKey.some(
          (part: unknown) => typeof part === 'object' && part !== null && JSON.stringify(part) === queryStr,
        ),
      );

      if (match) {
        return match.queryKey.filter((key): key is string => typeof key === 'string');
      }
    } catch {
      return undefined;
    }

    return undefined;
  }

  startRecording(): void {
    if (this.queryStore.getRecording()) {
      return;
    }

    this.queryStore.setRecording(true);
    this.resetCapturedQueries();
    this.cacheSubscriber.startListening();
    setupAxiosInterceptor({
      extractQueryKey: query => this.extractQueryKeyFromReactQuery(query),
      generateRequestId: () => this.generateRequestId(),
      getCurrentQueriesCount: () => this.queryStore.getCurrentCount(),
      getIsRecording: () => this.queryStore.getRecording(),
      getMaxQueries: () => this.queryStore.getMaxQueries(),
      onQueryCaptured: record => this.recordNetworkQuery(record),
    });

    this.queryStore.persistState();
    this.queryStore.notifyListeners();
  }

  stopRecording(): void {
    this.queryStore.setRecording(false);
    this.cacheSubscriber.stopListening();
    removeAxiosInterceptor();
    this.recentNetworkQueryHashes.clear();
    this.queryStore.persistState();
    this.queryStore.notifyListeners();
  }

  updatePagePath(pagePath: string): void {
    this.cacheSubscriber.updatePagePath(pagePath);
  }

  private resetCapturedQueries(): void {
    this.queryStore.clearQueries();
    this.cacheSubscriber.clearCapturedHashes();
    this.recentNetworkQueryHashes.clear();
    clearRequestTimings();
  }

  generateRequestId(): string {
    return `cube-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private recordNetworkQuery(record: CubeQueryRecord): void {
    if (this.queryStore.isFull()) return;

    const queryHash = JSON.stringify(record.query);
    const capturedAt = performance.now();
    this.recentNetworkQueryHashes.set(queryHash, [...(this.recentNetworkQueryHashes.get(queryHash) ?? []), capturedAt]);
    this.queryStore.addQuery(record);
  }

  private consumeRecentNetworkQuery(queryHash: string): boolean {
    const now = performance.now();
    const recentCaptures = (this.recentNetworkQueryHashes.get(queryHash) ?? []).filter(
      capturedAt => now - capturedAt <= NETWORK_CACHE_DUPLICATE_WINDOW_MS,
    );
    const oldestCapture = recentCaptures.shift();

    if (recentCaptures.length > 0) {
      this.recentNetworkQueryHashes.set(queryHash, recentCaptures);
    } else {
      this.recentNetworkQueryHashes.delete(queryHash);
    }

    return oldestCapture !== undefined;
  }
}
